import React, { useState, useEffect, useCallback } from 'react';
import { BsCalendar2WeekFill } from "react-icons/bs";
import { BsFillCalendarPlusFill } from "react-icons/bs";
import { Button, Form, Alert, Container, Row, Col } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';

export default function EditPayroll() {
  const { id } = useParams();
  const [showAlert, setShowAlert] = useState(false);
  const [payrollData, setPayrollData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isManualFinalSalary, setIsManualFinalSalary] = useState(false);
  const [formData, setFormData] = useState({
    basicSalary: '',
    totalWorkingDays: '',
    regularDaysWorked: '',
    lateMinutesDeduction: '',
    earlyCloseDeduction: '',
    extraMinutesBonus: '',
    leaveDeduction: '',
    workingUnits: '',
    unitPayRate: '',
    publicHolidayPay: '',
    holidayDaysWorked: '',
    finalSalary: '',
    adjustments: { amount: 0, description: '' },
    taxDeduction: { amount: 0, description: '' },
    providentFund: { amount: 0, description: '' }
  });

  const navigate = useNavigate();

  // Custom rounding function to match backend
  const customRound = (value) => {
    if (value === undefined || value === null) return 0;
    const num = Number(value);
    if (isNaN(num)) return 0;
    const decimal = num - Math.floor(num);
    return decimal >= 0.5 ? Math.ceil(num) : Math.floor(num);
  };

  // Calculate final salary based on form data (matches backend logic)
const calculateFinalSalary = useCallback((data) => {
  const {
    basicSalary,
    totalWorkingDays,
    regularDaysWorked,
    lateMinutesDeduction,
    earlyCloseDeduction,
    extraMinutesBonus,
    leaveDeduction,
    workingUnits,
    unitPayRate,
    publicHolidayPay,
    adjustments,
    taxDeduction,
    providentFund,
    finalSalary
  } = data;

  // If manually editing final salary, return that value directly
  if (isManualFinalSalary) {
    return customRound(Number(finalSalary) || 0);
  }

  // Convert all values to numbers (0 if invalid/empty)
  const basic = Number(basicSalary) || 0;
  const workingDays = Number(totalWorkingDays) || 0;
  const regDaysWorked = Number(regularDaysWorked) || 0;
  const lateDeduction = Number(lateMinutesDeduction) || 0;
  const earlyDeduction = Number(earlyCloseDeduction) || 0;
  const extraBonus = Number(extraMinutesBonus) || 0;
  const leaveDed = Number(leaveDeduction) || 0;
  const units = Number(workingUnits) || 0;
  const rate = Number(unitPayRate) || 0;
  const holidayPay = Number(publicHolidayPay) || 0;
  const adjAmount = Number(adjustments?.amount) || 0;
  const taxAmount = Number(taxDeduction?.amount) || 0;
  const pfAmount = Number(providentFund?.amount) || 0;

  // Calculate daily salary (protect against division by zero)
  const dailySalary = workingDays > 0 ? basic / workingDays : 0;

  // Calculate regular earned salary
  const regularEarnedSalary = dailySalary * regDaysWorked;

  // Calculate leave penalty
  const leavePenalty = leaveDed * dailySalary;

  // Calculate working units pay
  const workingUnitsPay = units * rate;

  // Calculate total deductions
  const totalDeductions = leavePenalty + lateDeduction + earlyDeduction;

  // Calculate salary after deductions
  const salaryAfterDeductions = regularEarnedSalary - totalDeductions;

  // Calculate gross salary (matches backend)
  const grossSalary = customRound(
    salaryAfterDeductions +
    extraBonus +
    workingUnitsPay +
    holidayPay +
    adjAmount
  );

  // Calculate net salary (tax deducted, PF added)
  const netSalary = grossSalary - taxAmount + pfAmount;

  return customRound(netSalary);
}, [isManualFinalSalary]); // Only depends on isManualFinalSalary
  // Update final salary whenever relevant fields change
  useEffect(() => {
    if (!payrollData) return;
    
    const finalSalary = calculateFinalSalary(formData);
    setFormData(prev => ({
      ...prev,
      finalSalary
    }));
  }, [
    formData.basicSalary,
    formData.totalWorkingDays,
    formData.regularDaysWorked,
    formData.lateMinutesDeduction,
    formData.earlyCloseDeduction,
    formData.extraMinutesBonus,
    formData.leaveDeduction,
    formData.workingUnits,
    formData.unitPayRate,
    formData.publicHolidayPay,
    formData.adjustments.amount,
    formData.taxDeduction.amount,
    formData.providentFund.amount,
    isManualFinalSalary,
    payrollData
  ]);

  // Fetch the payroll data when the component mounts
  useEffect(() => {
    const fetchPayrollData = async () => {
      try {
        const response = await fetch(`https://backend.ems.hiradentallab.com/api/payroll/payroll/${id}`);

        if (!response.ok) {
          throw new Error('Failed to fetch payroll data');
        }

        const result = await response.json();
        const actualData = result.data || result;
        setPayrollData(actualData);
        setIsManualFinalSalary(actualData.isFinalSalaryManual || false);

        const initialFormData = {
          basicSalary: actualData.basicSalary || '',
          totalWorkingDays: actualData.totalWorkingDays || '',
          regularDaysWorked: actualData.regularDaysWorked || '',
          lateMinutesDeduction: actualData.lateMinutesDeduction || '',
          earlyCloseDeduction: actualData.earlyCloseDeduction || '',
          extraMinutesBonus: actualData.extraMinutesBonus || '',
          leaveDeduction: actualData.leaveDeduction || '',
          workingUnits: actualData.workingUnits || '',
          unitPayRate: actualData.unitPayRate || '',
          publicHolidayPay: actualData.publicHolidayPay || '',
          holidayDaysWorked: actualData.holidayDaysWorked || '',
          finalSalary: actualData.finalSalary || '',
          adjustments: actualData.adjustments || { amount: 0, description: '' },
          taxDeduction: actualData.taxDeduction || { amount: 0, description: '' },
          providentFund: actualData.providentFund || { amount: 0, description: '' }
        };

        setFormData(initialFormData);
       
      } catch (error) {
        console.error('Error fetching payroll data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchPayrollData();
    }
  }, [id]);

  // Handle form data changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle adjustment changes
  const handleAdjustmentChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      adjustments: {
        ...prev.adjustments,
        [name]: name === 'amount' ? Number(value) : value
      }
    }));
  };

  // Handle tax deduction changes
  const handleTaxDeductionChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      taxDeduction: {
        ...prev.taxDeduction,
        [name]: name === 'amount' ? Number(value) : value
      }
    }));
  };

  // Handle provident fund changes
  const handleProvidentFundChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      providentFund: {
        ...prev.providentFund,
        [name]: name === 'amount' ? Number(value) : value
      }
    }));
  };

  // Handle final salary manual edit
  const handleFinalSalaryChange = (e) => {
    const { value } = e.target;
    setIsManualFinalSalary(true);
    setFormData(prev => ({
      ...prev,
      finalSalary: Number(value) || 0
    }));
  };

  // Handle form submission to update payroll
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare the data to send to the backend
      const payload = {
        ...formData,
        totalDaysWorked: (Number(formData.regularDaysWorked) || 0) + (Number(formData.holidayDaysWorked) || 0),
        isFinalSalaryManual: isManualFinalSalary
      };

      const response = await fetch(`https://backend.ems.hiradentallab.com/api/payroll/payroll/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update payroll data');
      }

      // Show success alert
      setShowAlert(true);

      // Redirect to payroll list after success
      setTimeout(() => {
        navigate('/emp-payroll');
      }, 2000);
     
    } catch (error) {
      console.error('Error updating payroll:', error);
      setShowAlert(false);
    }
  };

  // Loading state if data is being fetched
  if (isLoading) {
    return (
      <Container fluid>
        <h1 className='holiday-title'>
          <BsCalendar2WeekFill style={{ marginRight: '5px' }} />
          Public Holidays
        </h1>
        <hr />
        <h1 className='holiday-subtitle'>
          <BsFillCalendarPlusFill style={{ marginRight: '5px' }} />
          Edit Payroll
        </h1>
        <hr />
        <p>Loading payroll data...</p>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h1 className='title'>
        <BsFillCalendarPlusFill style={{ marginRight: '5px' }} />
        Edit Payroll
      </h1>
      <hr />

      {showAlert && (
        <Alert variant="success" onClose={() => setShowAlert(false)} dismissible>
          <Alert.Heading>Payroll Updated Successfully!</Alert.Heading>
        </Alert>
      )}

      <div className='form-container'>
        <Form onSubmit={handleSubmit} className='holiday-form'>
          {payrollData && (
            <>
              <Row className='form-row'>
                <Col md={4}>
                  <Form.Group controlId='basicSalary'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Basic Salary</Form.Label>
                    <Form.Control
                      type='number'
                      name="basicSalary"
                      value={formData.basicSalary}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId='totalWorkingDays'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Total Working Days</Form.Label>
                    <Form.Control
                      type='number'
                      name="totalWorkingDays"
                      value={formData.totalWorkingDays}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId='regularDaysWorked'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Regular Days Worked</Form.Label>
                    <Form.Control
                      type='number'
                      name="regularDaysWorked"
                      value={formData.regularDaysWorked}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className='form-row'>
                <Col md={4}>
                  <Form.Group controlId='lateMinutesDeduction'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Late Absent Deduction</Form.Label>
                    <Form.Control
                      type='number'
                      name="lateMinutesDeduction"
                      value={formData.lateMinutesDeduction}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId='earlyCloseDeduction'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Early Absent Deduction</Form.Label>
                    <Form.Control
                      type='number'
                      name="earlyCloseDeduction"
                      value={formData.earlyCloseDeduction}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId='extraMinutesBonus'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Overtime</Form.Label>
                    <Form.Control
                      type='number'
                      name="extraMinutesBonus"
                      value={formData.extraMinutesBonus}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className='form-row'>
                <Col md={4}>
                  <Form.Group controlId='workingUnits'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Working Units</Form.Label>
                    <Form.Control
                      type='number'
                      name="workingUnits"
                      value={formData.workingUnits}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId='unitPayRate'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Unit Pay Rate</Form.Label>
                    <Form.Control
                      type='number'
                      name="unitPayRate"
                      value={formData.unitPayRate}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId='publicHolidayPay'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Public Holiday Pay</Form.Label>
                    <Form.Control
                      type='number'
                      name="publicHolidayPay"
                      value={formData.publicHolidayPay}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className='form-row'>
                <Col md={4}>
                  <Form.Group controlId='adjustmentAmount'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Adjustment Amount</Form.Label>
                    <Form.Control
                      type='number'
                      name="amount"
                      value={formData.adjustments.amount}
                      onChange={handleAdjustmentChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId='taxDeductionAmount'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Tax Deduction Amount</Form.Label>
                    <Form.Control
                      type='number'
                      name="amount"
                      value={formData.taxDeduction.amount}
                      onChange={handleTaxDeductionChange}
                    />
                  </Form.Group>
                </Col>      
                <Col md={4}>
                  <Form.Group controlId='providentFundAmount'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Provident Fund Amount</Form.Label>
                    <Form.Control
                      type='number'
                      name="amount"
                      value={formData.providentFund.amount}
                      onChange={handleProvidentFundChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className='form-row'>
                <Col md={4}>
                  <Form.Group controlId='finalSalary'>
                    <Form.Label style={{ fontWeight: 'normal' }}>Final Salary</Form.Label>
                    <Form.Control
                      type='number'
                      name="finalSalary"
                      value={formData.finalSalary}
                      onChange={handleFinalSalaryChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
          <div className='button-content' style={{ marginTop: '10px' }}>
            <Button type='submit' variant='success'>Update</Button>
          </div>
        </Form>
      </div>
    </Container>
  );
}